import { NavLink, Outlet } from "react-router-dom";
import { BrainCircuit } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { UpsellPanel } from "@/components/upsell/UpsellPanel";
import { cn } from "@/lib/utils";
import { hasModuleEntitlement, useEntitlements } from "@/hooks/useEntitlements";

/**
 * The designed HIGH-INTENT module (Story N3). The E07A subscription gate is preserved
 * EXACTLY - same exact module code, same org-keyed rechecks, same denied and loading
 * copy - and the module is CAMPAIGN FIRST (story P2-UI): the index is the campaigns
 * board, each campaign has a detail route (Overview, Runs, Reports), and Leads search and
 * Settings remain available below them. Every screen is deep-linkable and the navigation
 * marks the current one.
 *
 * Story PS6 changed ONLY the denied branch's presentation: hidden navigation must never
 * mean invisible product, so the bare denial EmptyState is joined by the upsell panel
 * (contract C6's GET /me/upsell) that shows what the module does and raises a request.
 * The gate condition above it is byte-for-byte the audited E07A check.
 *
 * There is still no browser-side HIGH-INTENT service client: every read goes through the
 * app's own api base under /v1/radar/* (the N2 proxy seam), mocked at the seam in tests.
 */

const SECTIONS = [
  { to: "/high-intent", label: "Campaigns", end: true },
  { to: "/high-intent/leads", label: "Leads search", end: false },
  { to: "/high-intent/delivery", label: "Delivery", end: false },
  { to: "/high-intent/settings", label: "Settings", end: false },
];

export function HighIntentPage() {
  const { orgId } = useSession();
  const entitlements = useEntitlements();

  if (orgId === null) {
    return <EmptyState title="Choose an organisation first" body="HIGH-INTENT access is evaluated for the selected RADAR organisation." />;
  }
  if (entitlements.isLoading || entitlements.isFetching) {
    return <LoadingState label="Loading HIGH-INTENT access…" />;
  }
  if (entitlements.isError) return <ErrorState error={entitlements.error} onRetry={() => void entitlements.refetch()} />;
  if (!entitlements.isSuccess || !hasModuleEntitlement(entitlements.data, "high-intent")) {
    return (
      <div className="flex flex-col gap-6">
        <EmptyState title="HIGH-INTENT is not available for this organisation" body="Your organisation does not hold an active HIGH-INTENT subscription. Ask a RADAR administrator to review access." />
        <UpsellPanel focusModuleCode="high-intent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3">
        <BrainCircuit className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold text-ink">HIGH-INTENT</h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-muted">
            Intent signals for your subscribed campaigns.
          </p>
        </div>
      </div>

      <nav aria-label="HIGH-INTENT sections" className="flex gap-1 overflow-x-auto border-b border-line">
        {SECTIONS.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            end={section.end}
            className={({ isActive }) =>
              cn(
                "-mb-px inline-flex min-h-11 items-center whitespace-nowrap border-b-2 px-4 text-sm font-medium transition-colors",
                isActive
                  ? "border-primary text-primary"
                  : "border-transparent text-ink-muted hover:border-line hover:text-ink",
              )
            }
          >
            {section.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}
